import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type {
  Stack,
  StackCreate,
  StackUpdate,
  StackWithStats,
  StackSummary,
  StackBox,
  StackBoxCreate,
  StackBoxUpdate,
  BulkBoxOperation,
  BoxRange,
  StackCapacityInfo,
  AvailableBoxesResponse,
  BoxStatus,
  WormRecord,
  WormRecordCreate,
  WormRecordCreateBulk,
  FeedType,
  FeedTypeCreate,
  FeedingRecord,
  FeedingRecordCreate,
  QuickFeedingRecord,
  Customer,
  CustomerCreate,
  SalesOrder,
  SalesOrderCreate,
  BioghumusProduction,
  BioghumusProductionCreate,
  DashboardStats,
  FeedingStats,
  PaginatedResponse,
  ApiResponse,
} from '@/types';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '/api/v1',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Интерсептор для добавления токена к запросам
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Интерсепторы для обработки ответов и ошибок
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        console.error('API Error:', error);

        if (error.response?.status === 401) {
          // Обработка ошибок авторизации
          window.location.href = '/login';
        }

        return Promise.reject(error);
      }
    );
  }

  // Утилитарные методы (публичные для других сервисов)
  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: { timeout?: number }): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any, params?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data, { params });
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }

  // === СТЕЛЛАЖИ ===
  
  async getStacks(params?: { skip?: number; limit?: number; status?: string }): Promise<Stack[]> {
    return this.get<Stack[]>('/stacks', params);
  }

  async getStacksSummary(): Promise<StackSummary[]> {
    return this.get<StackSummary[]>('/stacks/summary');
  }

  async getStack(id: number): Promise<Stack> {
    return this.get<Stack>(`/stacks/${id}`);
  }

  async createStack(data: StackCreate): Promise<Stack> {
    return this.post<Stack>('/stacks', data);
  }

  async updateStack(id: number, data: StackUpdate): Promise<Stack> {
    return this.put<Stack>(`/stacks/${id}`, data);
  }

  async deleteStack(id: number): Promise<void> {
    console.log('🗑️ Удаляем стеллаж с ID:', id);
    try {
      await this.client.delete(`/stacks/${id}`);
      console.log('✅ Стеллаж удален успешно');
    } catch (error) {
      console.error('❌ Ошибка удаления стеллажа:', error);
      // Проверяем, не прошло ли удаление всё-таки
      if (error.response?.status === 204 || error.response?.status === 200) {
        console.log('✅ Удаление выполнено (статус 204/200)');
        return;
      }
      throw error;
    }
  }

  async getStackStats(id: number): Promise<StackWithStats> {
    return this.get<StackWithStats>(`/stacks/${id}/stats`);
  }

  async getStackByName(name: string): Promise<Stack> {
    return this.get<Stack>(`/stacks/name/${encodeURIComponent(name)}`);
  }

  async getStackCapacity(id: number): Promise<StackCapacityInfo> {
    return this.get<StackCapacityInfo>(`/stacks/${id}/capacity`);
  }

  // === ЯЩИКИ ===
  
  async getStackBoxes(stackId: number): Promise<StackBox[]> {
    return this.get<StackBox[]>(`/stacks/${stackId}/boxes`);
  }

  async getBoxByNumber(stackId: number, boxNumber: number): Promise<StackBox> {
    return this.get<StackBox>(`/stacks/${stackId}/boxes/${boxNumber}`);
  }

  async updateBox(boxId: number, data: {
    capacity_liters?: number;
    is_active?: boolean;
    notes?: string;
  }): Promise<StackBox> {
    const params = new URLSearchParams();
    if (data.capacity_liters !== undefined) params.append('capacity_liters', data.capacity_liters.toString());
    if (data.is_active !== undefined) params.append('is_active', data.is_active.toString());
    if (data.notes !== undefined) params.append('notes', data.notes);
    
    return this.put<StackBox>(`/stacks/boxes/${boxId}?${params.toString()}`);
  }

  async bulkBoxOperation(stackId: number, operation: Omit<BulkBoxOperation, 'stack_id'>): Promise<any> {
    return this.post<any>(`/stacks/${stackId}/boxes/bulk-operation`, operation);
  }

  async getAvailableBoxes(stackId: number, requiredCount: number = 1): Promise<AvailableBoxesResponse> {
    return this.get<AvailableBoxesResponse>(`/stacks/${stackId}/boxes/available`, { required_count: requiredCount });
  }

  async getBoxesStatus(stackId: number): Promise<{
    stack_id: number;
    stack_name: string;
    total_boxes: number;
    boxes: BoxStatus[];
  }> {
    return this.get(`/stacks/${stackId}/boxes/status`);
  }

  async createBoxRange(boxRange: BoxRange): Promise<{
    start_box: number;
    end_box: number;
    box_numbers: number[];
    count: number;
  }> {
    return this.post('/stacks/box-range', boxRange);
  }


  async addBoxesToStack(stackId: number, count: number, capacityLiters: number = 20.0): Promise<{
    success: boolean;
    message: string;
    stack_id: number;
    added_boxes: Array<{
      box_number: number;
      capacity_liters: number;
    }>;
    new_total_boxes: number;
  }> {
    return this.post(`/stacks/${stackId}/boxes/add?count=${count}&capacity_liters=${capacityLiters}`);
  }

  async removeBoxFromStack(stackId: number, boxId: number, force: boolean = false): Promise<{
    success: boolean;
    message: string;
    stack_id: number;
    removed_box_id: number;
    removed_box_number: number;
    new_total_boxes: number;
  }> {
    return this.delete(`/stacks/${stackId}/boxes/${boxId}?force=${force}`);
  }

  async removeMultipleBoxes(stackId: number, boxIds: number[], force: boolean = false): Promise<{
    success: boolean;
    message: string;
    stack_id: number;
    removed_boxes: Array<{
      box_id: number;
      box_number: number;
    }>;
    new_total_boxes: number;
  }> {
    return this.post(`/stacks/${stackId}/boxes/remove-multiple?force=${force}`, { box_ids: boxIds });
  }

  // === ЗАПИСИ ЧЕРВЕЙ ===
  
  async getWormRecords(params?: {
    skip?: number;
    limit?: number;
    stack_id?: number;
    box_id?: number;
    worm_stage?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<WormRecord[]> {
    return this.get<WormRecord[]>('/worm-records', params);
  }

  async getWormRecord(id: number): Promise<WormRecord> {
    return this.get<WormRecord>(`/worm-records/${id}`);
  }

  async createWormRecord(data: WormRecordCreate): Promise<WormRecord> {
    return this.post<WormRecord>('/worm-records', data);
  }

  async createWormRecordBulk(data: WormRecordCreateBulk): Promise<WormRecord> {
    return this.post<WormRecord>('/worm-records/bulk', data);
  }

  async updateWormRecord(id: number, data: Partial<WormRecordCreate>): Promise<WormRecord> {
    return this.put<WormRecord>(`/worm-records/${id}`, data);
  }

  async deleteWormRecord(id: number): Promise<void> {
    return this.delete<void>(`/worm-records/${id}`);
  }

  async getLatestWormRecord(stackId: number): Promise<WormRecord | null> {
    return this.get<WormRecord | null>(`/worm-records/stack/${stackId}/latest`);
  }

  async getWormRecordsByBox(stackId: number, boxNumber: number): Promise<WormRecord[]> {
    return this.get<WormRecord[]>(`/worm-records/stack/${stackId}/box/${boxNumber}`);
  }

  async getStackBoxStats(stackId: number): Promise<any> {
    return this.get<any>(`/worm-records/stack/${stackId}/box-stats`);
  }

  async getStackBoxRecords(stackId: number, recordDate?: string): Promise<any> {
    const params = recordDate ? { record_date: recordDate } : undefined;
    return this.get<any>(`/worm-records/stack/${stackId}/box-records`, params);
  }

  async getStackWormHistory(stackId: number, limit: number = 10): Promise<WormRecord[]> {
    return this.get<WormRecord[]>(`/worm-records/stack/${stackId}/history`, { limit });
  }

  // === ТИПЫ КОРМОВ ===
  
  async getFeedTypes(): Promise<FeedType[]> {
    return this.get<FeedType[]>('/feed-types');
  }

  async getFeedType(id: number): Promise<FeedType> {
    return this.get<FeedType>(`/feed-types/${id}`);
  }

  async createFeedType(data: FeedTypeCreate): Promise<FeedType> {
    return this.post<FeedType>('/feed-types', data);
  }

  async updateFeedType(id: number, data: Partial<FeedTypeCreate>): Promise<FeedType> {
    return this.put<FeedType>(`/feed-types/${id}`, data);
  }

  async deleteFeedType(id: number): Promise<void> {
    return this.delete<void>(`/feed-types/${id}`);
  }

  // === КОРМЛЕНИЕ ===
  
  async getFeedingRecords(params?: {
    skip?: number;
    limit?: number;
    stack_id?: number;
    feed_type_id?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<FeedingRecord[]> {
    return this.get<FeedingRecord[]>('/feeding', params);
  }

  async getFeedingRecord(id: number): Promise<FeedingRecord> {
    return this.get<FeedingRecord>(`/feeding/${id}`);
  }

  async createFeedingRecord(data: FeedingRecordCreate): Promise<FeedingRecord> {
    return this.post<FeedingRecord>('/feeding', data);
  }

  async createQuickFeedingRecord(data: QuickFeedingRecord): Promise<FeedingRecord> {
    return this.post<FeedingRecord>('/feeding/quick', data);
  }

  async updateFeedingRecord(id: number, data: Partial<FeedingRecordCreate>): Promise<FeedingRecord> {
    return this.put<FeedingRecord>(`/feeding/${id}`, data);
  }

  async deleteFeedingRecord(id: number): Promise<void> {
    return this.delete<void>(`/feeding/${id}`);
  }

  async getFeedingStats(): Promise<FeedingStats> {
    return this.get<FeedingStats>('/feeding/statistics');
  }

  async getFeedingSchedule(): Promise<any[]> {
    return this.get<any[]>('/feeding/schedule');
  }

  async getTodayFeedingSummary(): Promise<any> {
    return this.get<any>('/feeding/today/summary');
  }

  // === КЛИЕНТЫ ===
  
  async getCustomers(params?: { skip?: number; limit?: number; search?: string }): Promise<Customer[]> {
    return this.get<Customer[]>('/customers', params);
  }

  async getCustomer(id: number): Promise<Customer> {
    return this.get<Customer>(`/customers/${id}`);
  }

  async createCustomer(data: CustomerCreate): Promise<Customer> {
    return this.post<Customer>('/customers', data);
  }

  async updateCustomer(id: number, data: Partial<CustomerCreate>): Promise<Customer> {
    return this.put<Customer>(`/customers/${id}`, data);
  }

  async deleteCustomer(id: number): Promise<void> {
    return this.delete<void>(`/customers/${id}`);
  }

  async getCustomerStats(id: number): Promise<any> {
    return this.get<any>(`/customers/${id}/stats`);
  }

  async getTopCustomers(limit: number = 10): Promise<any[]> {
    return this.get<any[]>('/customers/top', { limit });
  }

  // === ЗАКАЗЫ ===
  
  async getSalesOrders(params?: {
    skip?: number;
    limit?: number;
    status?: string;
    customer_id?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<SalesOrder[]> {
    return this.get<SalesOrder[]>('/sales-orders', params);
  }

  async getSalesOrder(id: number): Promise<SalesOrder> {
    return this.get<SalesOrder>(`/sales-orders/${id}`);
  }

  async createSalesOrder(data: SalesOrderCreate): Promise<SalesOrder> {
    return this.post<SalesOrder>('/sales-orders', data);
  }

  async updateSalesOrder(id: number, data: Partial<SalesOrderCreate>): Promise<SalesOrder> {
    return this.put<SalesOrder>(`/sales-orders/${id}`, data);
  }

  async deleteSalesOrder(id: number): Promise<void> {
    return this.delete<void>(`/sales-orders/${id}`);
  }

  async getSalesStats(): Promise<any> {
    return this.get<any>('/sales-orders/stats');
  }

  async getNotifications(): Promise<any> {
    return this.get<any>('/sales-orders/notifications');
  }

  async getTodaysOrders(): Promise<SalesOrder[]> {
    return this.get<SalesOrder[]>('/sales-orders/today');
  }

  async getArchivedOrders(params?: {
    skip?: number;
    limit?: number;
    customer_id?: number;
  }): Promise<{ orders: SalesOrder[]; total: number }> {
    return this.get<{ orders: SalesOrder[]; total: number }>('/sales-orders/archive', params);
  }

  async updateOrderStatus(orderId: number, status: string): Promise<SalesOrder> {
    return this.patch<SalesOrder>(`/sales-orders/${orderId}/status`, null, { status });
  }

  async updatePaymentStatus(orderId: number, paymentStatus: string, paymentMethod?: string): Promise<SalesOrder> {
    return this.patch<SalesOrder>(`/sales-orders/${orderId}/payment`, null, {
      payment_status: paymentStatus,
      payment_method: paymentMethod
    });
  }

  // === Платежи ===
  async getPayments(params?: {
    skip?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
    customer_id?: number;
  }): Promise<any[]> {
    return this.get<any[]>('/payments/', params);
  }

  async getPaymentStats(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<any> {
    return this.get<any>('/payments/stats', params);
  }

  async getAvailableWarehouseStock(quality?: string): Promise<any> {
    const params = quality ? { quality } : undefined;
    return this.get<any>('/sales-orders/available-stock', params);
  }

  async createWarehouseOrder(data: any): Promise<any> {
    return this.post<any>('/sales-orders/warehouse-order', data);
  }

  // Новые методы для удаления заказов
  async deleteAllSalesOrders(): Promise<{ message: string; deleted_count: number }> {
    return this.delete<{ message: string; deleted_count: number }>('/sales-orders/all');
  }

  async forceDeleteSalesOrder(id: number): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`/sales-orders/${id}/force`);
  }

  // === Расходы ===
  async getExpenses(params?: {
    skip?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
    category_id?: number;
  }): Promise<any[]> {
    return this.get<any[]>('/expenses/', params);
  }

  async getExpenseStats(params?: { start_date?: string; end_date?: string }): Promise<any> {
    return this.get<any>('/expenses/stats', params);
  }

  async createExpense(data: any): Promise<any> {
    return this.post<any>('/expenses/', data);
  }

  async updateExpense(id: number, data: any): Promise<any> {
    return this.put<any>(`/expenses/${id}`, data);
  }

  async deleteExpense(id: number): Promise<any> {
    return this.delete<any>(`/expenses/${id}`);
  }

  async getExpenseCategories(): Promise<any[]> {
    return this.get<any[]>('/expenses/categories');
  }

  async createExpenseCategory(data: any): Promise<any> {
    return this.post<any>('/expenses/categories', data);
  }

  async updateExpenseCategory(id: number, data: any): Promise<any> {
    return this.put<any>(`/expenses/categories/${id}`, data);
  }

  async deleteExpenseCategory(id: number): Promise<any> {
    return this.delete<any>(`/expenses/categories/${id}`);
  }

  async suggestExpenseCategory(description: string): Promise<any> {
    return this.get<any>('/expenses/suggest-category', { description });
  }

  // === БИОГУМУС ===
  
  async getBioghumusProduction(params?: {
    skip?: number;
    limit?: number;
    stack_id?: number;
    status?: string;
  }): Promise<BioghumusProduction[]> {
    return this.get<BioghumusProduction[]>('/biohumus-production', params);
  }

  async getBioghumusRecord(id: number): Promise<BioghumusProduction> {
    return this.get<BioghumusProduction>(`/biohumus-production/${id}`);
  }

  async createBioghumusRecord(data: BioghumusProductionCreate): Promise<BioghumusProduction> {
    return this.post<BioghumusProduction>('/biohumus-production', data);
  }

  async updateBioghumusRecord(id: number, data: Partial<BioghumusProductionCreate>): Promise<BioghumusProduction> {
    return this.put<BioghumusProduction>(`/biohumus-production/${id}`, data);
  }

  async deleteBioghumusRecord(id: number): Promise<void> {
    return this.delete<void>(`/biohumus-production/${id}`);
  }

  async getReadyBiohumus(): Promise<BioghumusProduction[]> {
    return this.get<BioghumusProduction[]>('/biohumus-production/ready');
  }

  async getBioghumusStats(): Promise<any> {
    return this.get<any>('/biohumus-production/stats');
  }

  // === DASHBOARD ===

  async getDashboardStats(): Promise<DashboardStats> {
    return this.get<DashboardStats>('/dashboard/stats');
  }

  async getStackOccupancy(): Promise<{
    filled: {
      stack_count: number;
      stacks: Array<{ id: number; name: string; box_count: number }>;
      total_boxes: number;
    };
    empty: {
      stack_count: number;
      stacks: Array<{ id: number; name: string; box_count: number }>;
      total_boxes: number;
    };
    partial: {
      stack_count: number;
      stacks: Array<{ id: number; name: string; total_boxes: number; filled_boxes: number; free_boxes: number }>;
      total_free_boxes: number;
    };
  }> {
    return this.get('/dashboard/stack-occupancy');
  }

  async getRecentActivity(): Promise<any[]> {
    return this.get<any[]>('/dashboard/activity');
  }

  async getAlertsAndNotifications(): Promise<any[]> {
    return this.get<any[]>('/dashboard/alerts');
  }

  // === ПАРТИИ ЧЕРВЕЙ (BATCHES) ===
  
  async getBatches(params?: {
    page?: number;
    page_size?: number;
    is_active?: boolean;
    stage?: string;
    stack_id?: number;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    console.log('🔍 API: Запрос партий с параметрами:', params);
    // ИСПРАВЛЕНИЕ: используем правильный роут /batches (корневой GET)
    return this.get<any>('/batches', params);
  }

  async getActiveBatches(): Promise<any[]> {
    return this.get<any[]>('/batches/active');
  }

  async getBatch(batchId: number): Promise<any> {
    return this.get<any>(`/batches/${batchId}`);
  }

  async createBatch(data: any): Promise<any> {
    return this.post<any>('/batches', data);
  }

  async updateBatch(batchId: number, data: any): Promise<any> {
    return this.put<any>(`/batches/${batchId}`, data);
  }

  async deleteBatch(batchId: number): Promise<void> {
    return this.delete<void>(`/batches/${batchId}`);
  }

  async splitBatch(batchId: number, data: any): Promise<any[]> {
    return this.post<any[]>(`/batches/${batchId}/split`, data);
  }

  async moveBatch(batchId: number, data: any): Promise<any> {
    return this.post<any>(`/batches/${batchId}/move`, data);
  }

  async changeBatchStage(batchId: number, data: any): Promise<any> {
    return this.post<any>(`/batches/${batchId}/change-stage`, data);
  }

  async addBatchOperation(batchId: number, data: any): Promise<any> {
    return this.post<any>(`/batches/${batchId}/operation`, data);
  }

  async getBatchTimeline(batchId: number): Promise<any> {
    return this.get<any>(`/batches/${batchId}/timeline`);
  }

  async getBatchMovements(batchId: number): Promise<any[]> {
    return this.get<any[]>(`/batches/${batchId}/movements`);
  }

  async getBatchOperations(batchId: number): Promise<any[]> {
    return this.get<any[]>(`/batches/${batchId}/operations`);
  }

  async getBatchStatistics(batchId: number): Promise<any> {
    return this.get<any>(`/batches/${batchId}/statistics`);
  }

  async getBatchStageTransitions(batchId: number): Promise<any> {
    return this.get<any>(`/batches/${batchId}/stage-transitions`);
  }

  async createBulkBatchOperations(operations: any[]): Promise<any[]> {
    return this.post<any[]>('/batches/bulk/operations', operations);
  }

  // Отладочные методы для батчей
  async testBatchesEndpoint(): Promise<any> {
    return this.get<any>('/batches/test');
  }

  async debugBatchesTables(): Promise<any> {
    return this.get<any>('/batches/debug/tables');
  }

  async debugBatchesModels(): Promise<any> {
    return this.get<any>('/batches/debug/models');
  }

  async createTestBatch(): Promise<any> {
    return this.post<any>('/batches/debug/create-test-batch');
  }

  // === АНАЛИТИКА ===
  
  async getTemperatureData(days: number = 7): Promise<any[]> {
    return this.get<any[]>('/analytics/temperature', { days });
  }

  async getSalesAnalytics(period: 'week' | 'month' | 'year' = 'month'): Promise<any> {
    return this.get<any>('/analytics/sales', { period });
  }

  async getFeedingAnalytics(period: 'week' | 'month' | 'year' = 'month'): Promise<any> {
    return this.get<any>('/analytics/feeding', { period });
  }

  async getProductionAnalytics(period: 'week' | 'month' | 'year' = 'month'): Promise<any> {
    return this.get<any>('/analytics/production', { period });
  }

  // === СИСТЕМНЫЕ НАСТРОЙКИ ===
  
  async getSystemSettings(): Promise<any> {
    return this.get<any>('/system-settings/');
  }

  async updateSystemSettings(data: any): Promise<any> {
    return this.put<any>('/system-settings/', data);
  }

  async updateFarmInfo(farmName: string, farmArea: number, rackCount: number): Promise<any> {
    const params = new URLSearchParams();
    params.append('farm_name', farmName);
    params.append('farm_area', farmArea.toString());
    params.append('rack_count', rackCount.toString());
    return this.put<any>(`/system-settings/farm-info?${params.toString()}`);
  }

  async updateTimezone(timezone: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('timezone', timezone);
    return this.put<any>(`/system-settings/timezone?${params.toString()}`);
  }

  async updateTheme(theme: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('theme', theme);
    return this.put<any>(`/system-settings/theme?${params.toString()}`);
  }

  async updateNotificationSettings(settings: any): Promise<any> {
    return this.put<any>('/system-settings/notifications', settings);
  }

  async updateVoiceSettings(voiceEnabled?: boolean, sensitivity?: number, activationWord?: string): Promise<any> {
    const params = new URLSearchParams();
    if (voiceEnabled !== undefined) params.append('voice_enabled', voiceEnabled.toString());
    if (sensitivity !== undefined) params.append('voice_sensitivity', sensitivity.toString());
    if (activationWord !== undefined) params.append('voice_activation_word', activationWord);
    
    return this.put<any>(`/system-settings/voice?${params.toString()}`);
  }

  async updateBackupSettings(frequency?: string, retentionDays?: number): Promise<any> {
    const params = new URLSearchParams();
    if (frequency !== undefined) params.append('backup_frequency', frequency);
    if (retentionDays !== undefined) params.append('backup_retention_days', retentionDays.toString());
    
    return this.put<any>(`/system-settings/backup?${params.toString()}`);
  }

  async getSettingsSummary(): Promise<any> {
    return this.get<any>('/system-settings/summary');
  }

  async getNotificationPreferences(): Promise<any> {
    return this.get<any>('/system-settings/notification-preferences');
  }

  async updateNotificationPreference(notificationType: string, preferenceData: any): Promise<any> {
    return this.put<any>(`/system-settings/notification-preferences/${notificationType}`, preferenceData);
  }

  async getAvailableTimezones(): Promise<any> {
    return this.get<any>('/system-settings/timezones');
  }

  async getAvailableLanguages(): Promise<any> {
    return this.get<any>('/system-settings/languages');
  }

  async getAvailableThemes(): Promise<any> {
    return this.get<any>('/system-settings/themes');
  }

  async getAvailableSubstrates(): Promise<any> {
    return this.get<any>('/system-settings/substrates');
  }

  // === TELEGRAM НАСТРОЙКИ ===

  async updateTelegramSettings(data: {
    telegram_notifications?: boolean;
    telegram_bot_token?: string;
    telegram_recipient_user_ids?: string;
  }): Promise<any> {
    return this.put<any>('/system-settings/telegram', data);
  }

  async testTelegramNotification(message?: string): Promise<any> {
    return this.post<any>('/system-settings/telegram/test', {
      message: message || 'Тестовое сообщение от WormTracker'
    });
  }

  async getTelegramRecipients(): Promise<{
    users: Array<{
      id: number;
      username: string;
      full_name: string | null;
      telegram_chat_id: string | null;
    }>;
  }> {
    return this.get<any>('/system-settings/telegram/recipients');
  }

  // === EMAIL НАСТРОЙКИ ===

  async updateEmailSettings(data: {
    email_notifications?: boolean;
    smtp_server?: string;
    smtp_port?: number;
    smtp_username?: string;
    smtp_password?: string;
    smtp_use_tls?: boolean;
    smtp_use_ssl?: boolean;
    email_from_address?: string;
    email_from_name?: string;
    email_recipient_user_ids?: string;
  }): Promise<any> {
    return this.put<any>('/system-settings/email', data);
  }

  async testEmailNotification(message?: string): Promise<any> {
    return this.post<any>('/system-settings/email/test', {
      message: message || 'Тестовое сообщение от WormTracker'
    });
  }

  async getEmailRecipients(): Promise<{
    users: Array<{
      id: number;
      username: string;
      full_name: string | null;
      email: string | null;
    }>;
  }> {
    return this.get<any>('/system-settings/email/recipients');
  }

  // === СКЛАД - УДАЛЕНИЕ ===
  async deleteAllWarehouseStock(): Promise<{ message: string; deleted_count: number }> {
    return this.delete<{ message: string; deleted_count: number }>('/warehouse/stock/all');
  }

  async deleteWarehouseStockItem(stockId: number, productType?: string): Promise<{ message: string }> {
    // Не передаем product_type из-за проблем с URL-encoded кириллицей в FastAPI
    // Сервер сам определит тип по ID (>= 100000 = биогумус)
    return this.delete<{ message: string }>(`/warehouse/stock/${stockId}`);
  }

  // === СКЛАД - АНАЛИТИКА ===
  async getWarehouseBatchAnalytics(): Promise<any> {
    return this.get<any>('/warehouse/analytics/batch-history');
  }

  // === БОТ ЗАКАЗОВ ===

  // Настройки бота
  // === T-BANK МОНИТОРИНГ ВЫПИСКИ ===

  async getTBankStatementSettings(): Promise<any> {
    return this.get<any>('/system-settings/tbank-statement');
  }

  async updateTBankStatementSettings(data: {
    tbank_statement_enabled?: boolean;
    tbank_statement_token?: string;
    tbank_statement_account?: string;
    tbank_sync_start_date?: string;
    tbank_owner_personal_account?: string;
    tbank_unmatched_notify?: boolean;
  }): Promise<any> {
    return this.put<any>('/system-settings/tbank-statement', data);
  }

  async forceTBankSync(): Promise<any> {
    return this.post<any>('/tbank/sync', {}, { timeout: 60000 });
  }

  async getTBankTransactions(params?: {
    tx_type?: string;
    match_status?: string;
    limit?: number;
  }): Promise<any[]> {
    const q = new URLSearchParams();
    if (params?.tx_type) q.append('tx_type', params.tx_type);
    if (params?.match_status) q.append('match_status', params.match_status);
    if (params?.limit) q.append('limit', String(params.limit));
    return this.get<any[]>(`/tbank/transactions?${q.toString()}`);
  }

  async getTBankBalance(): Promise<any> {
    return this.get<any>('/tbank/balance');
  }

  async getBotSettings(): Promise<any> {
    return this.get<any>('/bot/settings');
  }

  async updateBotSettings(data: any): Promise<any> {
    return this.put<any>('/bot/settings', data);
  }

  // Статус и управление
  async getBotStatus(): Promise<{ status: string; uptime: string | null }> {
    return this.get<any>('/bot/status');
  }

  async startBot(): Promise<any> {
    return this.post<any>('/bot/start');
  }

  async stopBot(): Promise<any> {
    return this.post<any>('/bot/stop');
  }

  async restartBot(): Promise<any> {
    return this.post<any>('/bot/restart');
  }

  // Max-бот заказов
  async getMaxBotSettings(): Promise<any> {
    return this.get<any>('/bot/max/settings');
  }

  async updateMaxBotSettings(data: any): Promise<any> {
    return this.put<any>('/bot/max/settings', data);
  }

  async getMaxBotStatus(): Promise<{ status: string; uptime: string | null }> {
    return this.get<any>('/bot/max/status');
  }

  async startMaxBot(): Promise<any> {
    return this.post<any>('/bot/max/start');
  }

  async stopMaxBot(): Promise<any> {
    return this.post<any>('/bot/max/stop');
  }

  async restartMaxBot(): Promise<any> {
    return this.post<any>('/bot/max/restart');
  }

  // Товары бота
  async getBotProducts(params?: { category?: string; active_only?: boolean }): Promise<any[]> {
    return this.get<any[]>('/bot/products', params);
  }

  async getBotProduct(id: number): Promise<any> {
    return this.get<any>(`/bot/products/${id}`);
  }

  async createBotProduct(data: any): Promise<any> {
    return this.post<any>('/bot/products', data);
  }

  async updateBotProduct(id: number, data: any): Promise<any> {
    return this.put<any>(`/bot/products/${id}`, data);
  }

  async deleteBotProduct(id: number): Promise<any> {
    return this.delete<any>(`/bot/products/${id}`);
  }

  // === OPS-API (location-first операции) ===

  async opsGetStack(stackId: number): Promise<OpsStackContent> {
    return this.get<OpsStackContent>(`/ops/stack/${stackId}`);
  }

  async opsGetBox(stackId: number, boxNumber: number): Promise<OpsBoxContent> {
    return this.get<OpsBoxContent>(`/ops/stack/${stackId}/box/${boxNumber}`);
  }

  async opsSettle(data: OpsSettleRequest): Promise<OpsSettleResponse> {
    return this.post<OpsSettleResponse>('/ops/settle', data);
  }

  async opsWeigh(data: OpsWeighRequest): Promise<OpsWeighResponse> {
    return this.post<OpsWeighResponse>('/ops/weigh', data);
  }

  async opsMove(data: OpsMoveRequest): Promise<OpsMoveResponse> {
    return this.post<OpsMoveResponse>('/ops/move', data);
  }

  async opsScreen(data: OpsScreenRequest): Promise<OpsScreenResponse> {
    return this.post<OpsScreenResponse>('/ops/screen', data);
  }

  async opsFeed(data: OpsFeedRequest): Promise<OpsFeedResponse> {
    return this.post<OpsFeedResponse>('/ops/feed', data);
  }

  async opsLoss(data: OpsLossRequest): Promise<OpsLossResponse> {
    return this.post<OpsLossResponse>('/ops/loss', data);
  }

  async opsHarvest(data: OpsHarvestRequest): Promise<OpsHarvestResponse> {
    return this.post<OpsHarvestResponse>('/ops/harvest', data);
  }

  async opsPackage(data: OpsPackageRequest): Promise<OpsPackageResponse> {
    return this.post<OpsPackageResponse>('/ops/package', data);
  }
}

// Создаем единственный экземпляр API клиента
export const apiClient = new ApiClient();

// Экспортируем отдельные методы для удобства
export const {
  // Стеллажи
  getStacks,
  getStacksSummary,
  getStack,
  createStack,
  updateStack,
  deleteStack,
  getStackStats,
  getStackByName,
  getStackCapacity,
  
  // Ящики
  getStackBoxes,
  getBoxByNumber,
  updateBox,
  bulkBoxOperation,
  getAvailableBoxes,
  getBoxesStatus,
  createBoxRange,
  
  // Записи червей
  getWormRecords,
  getWormRecord,
  createWormRecord,
  createWormRecordBulk,
  updateWormRecord,
  deleteWormRecord,
  getLatestWormRecord,
  getWormRecordsByBox,
  getStackBoxStats,
  getStackBoxRecords,
  
  // Корма
  getFeedTypes,
  getFeedType,
  createFeedType,
  updateFeedType,
  deleteFeedType,
  
  // Кормление
  getFeedingRecords,
  getFeedingRecord,
  createFeedingRecord,
  createQuickFeedingRecord,
  updateFeedingRecord,
  deleteFeedingRecord,
  getFeedingStats,
  getFeedingSchedule,
  getTodayFeedingSummary,
  
  // Клиенты
  getCustomers,
  getCustomer,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  getCustomerStats,
  getTopCustomers,
  
  // Заказы
  getSalesOrders,
  getSalesOrder,
  createSalesOrder,
  updateSalesOrder,
  updateOrderStatus,
  updatePaymentStatus,
  deleteSalesOrder,
  getSalesStats,

  // Платежи
  getPayments,
  getPaymentStats,
  getTodaysOrders,
  
  // Биогумус
  getBioghumusProduction,
  getBioghumusRecord,
  createBioghumusRecord,
  updateBioghumusRecord,
  deleteBioghumusRecord,
  getReadyBiohumus,
  getBioghumusStats,
  
  // Dashboard
  getDashboardStats,
  getStackOccupancy,
  getRecentActivity,
  getAlertsAndNotifications,
  
  // Партии червей
  getBatches,
  getActiveBatches,
  getBatch,
  createBatch,
  updateBatch,
  deleteBatch,
  splitBatch,
  moveBatch,
  changeBatchStage,
  addBatchOperation,
  getBatchTimeline,
  getBatchMovements,
  getBatchOperations,
  getBatchStatistics,
  getBatchStageTransitions,
  createBulkBatchOperations,
  testBatchesEndpoint,
  debugBatchesTables,
  debugBatchesModels,
  createTestBatch,
  
  // Аналитика
  getTemperatureData,
  getSalesAnalytics,
  getFeedingAnalytics,
  getProductionAnalytics,
  
  // Системные настройки
  getSystemSettings,
  updateSystemSettings,
  updateFarmInfo,
  updateTimezone,
  updateTheme,
  updateNotificationSettings,
  updateVoiceSettings,
  updateBackupSettings,
  getSettingsSummary,
  getNotificationPreferences,
  updateNotificationPreference,
  getAvailableTimezones,
  getAvailableLanguages,
  getAvailableThemes,
  getAvailableSubstrates,

  // Telegram
  updateTelegramSettings,
  testTelegramNotification,
  getTelegramRecipients,

  // Email
  updateEmailSettings,
  testEmailNotification,
  getEmailRecipients,

  // Бот заказов (Telegram)
  getBotSettings,
  updateBotSettings,
  getBotStatus,
  startBot,
  stopBot,
  restartBot,

  // Max-бот заказов
  getMaxBotSettings,
  updateMaxBotSettings,
  getMaxBotStatus,
  startMaxBot,
  stopMaxBot,
  restartMaxBot,

  // Товары бота
  getBotProducts,
  getBotProduct,
  createBotProduct,
  updateBotProduct,
  deleteBotProduct,

  // Расходы
  getExpenses,
  getExpenseStats,
  createExpense,
  updateExpense,
  deleteExpense,
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
  suggestExpenseCategory,
} = apiClient;

export default apiClient;
export const api = apiClient; // Именованный экспорт для совместимости
